import { type Href, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import DateTimePicker from '@/components/Custom/DateTimePicker';
import Box from '@/components/Unistyles/Box';
import Text from '@/components/Unistyles/Text';
import TextInput from '@/components/Unistyles/TextInput';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
  WelcomeSecondaryAction,
} from '@/components/Welcome/WelcomeUI';
import {
  getWelcomeScheduleSlotCount,
  type WelcomeFormValues,
} from '@/layouts/Welcome/form';
import { WELCOME_COMPLETION_ROUTE } from '@/layouts/Welcome/navigation';

type WelcomeOrgScheduleSlotsProps = {
  completionRoute?: Href;
  optionsRoute?: Href;
};

export default function WelcomeOrgScheduleSlots({
  completionRoute = WELCOME_COMPLETION_ROUTE,
  optionsRoute = '/welcome/org/schedule/options',
}: WelcomeOrgScheduleSlotsProps) {
  const { navigate } = useRouter();
  const { control, setValue } = useFormContext<WelcomeFormValues>();
  const rows = useWatch({ control, name: 'scheduleSlots' });
  const teamRows = useWatch({ control, name: 'orgTeamRows' });
  const teamCount = teamRows.filter(
    (teamRow) => teamRow.title.trim().length > 0,
  ).length;
  const slotCount = getWelcomeScheduleSlotCount(teamCount);
  const hasFilledSlot = rows.some((row) => row.datetime.trim().length > 0);
  const hasInvalidSlot = rows.some((row) => {
    const datetime = row.datetime.trim();

    return datetime.length > 0 && !DateTime.fromISO(datetime).isValid;
  });
  const isValid = hasFilledSlot && !hasInvalidSlot;

  const setRows = (nextRows: WelcomeFormValues['scheduleSlots']) => {
    setValue('scheduleSlots', nextRows, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('generatedScheduleGames', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onChangeRow = (
    rowId: string,
    field: 'datetime' | 'location',
    value: string,
  ) => {
    setRows(
      rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    );
  };

  const onPressContinue = () => {
    if (!isValid) {
      return;
    }

    navigate(optionsRoute);
  };
  const onPressSkipSchedule = () => {
    setValue('generatedScheduleGames', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
    navigate(completionRoute);
  };

  return (
    <WelcomeStepFrame
      title="Set the first week of games"
      subtitle={`Start with the first game date and weekly game slots. ${teamCount} teams create ${slotCount} slot${slotCount === 1 ? '' : 's'} per game week.`}
      footer={
        <Box space="sm" style={styles.footer}>
          <WelcomeCard>
            <Box
              direction="horizontal"
              alignItems="center"
              justifyContent="space-between"
              space="md"
            >
              <Text bold flexShrink={1}>
                Number of Time Slots
              </Text>
              <Text
                bold
                size="lg"
                selectable={false}
                style={styles.countText}
                testID="welcome-schedule-slot-count"
              >
                {rows.length}
              </Text>
            </Box>
          </WelcomeCard>
          <WelcomeActions>
            <WelcomeSecondaryAction
              label="Skip schedule"
              icon="arrow-forward-outline"
              onPress={onPressSkipSchedule}
            />
            <WelcomePrimaryAction
              label="Continue"
              disabled={!isValid}
              onPress={onPressContinue}
            />
          </WelcomeActions>
        </Box>
      }
    >
      <WelcomeStepScrollContent>
        {rows.map((row, index) => (
          <WelcomeCard key={row.id}>
            <Text bold variant="typographySecondary">
              {`Slot ${index + 1}`}
            </Text>
            <DateTimePicker
              value={row.datetime}
              onChange={(value) => onChangeRow(row.id, 'datetime', value ?? '')}
              testID={`welcome-schedule-slot-${index}-datetime`}
            />
            <TextInput
              enterKeyHint="done"
              placeholder="Location, optional"
              value={row.location}
              onChangeText={(value) => onChangeRow(row.id, 'location', value)}
            />
          </WelcomeCard>
        ))}
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}

const styles = StyleSheet.create((theme) => ({
  countText: {
    fontVariant: ['tabular-nums'],
  },
  footer: {
    alignSelf: 'center',
    maxWidth: theme.size(132),
    width: '100%',
  },
}));
